import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Download, RotateCcw, Share2, X, ZoomIn, ZoomOut } from "lucide-react";
import { toast } from "sonner";

interface FullScreenImageProps {
  images: string[];
  currentIndex: number;
  onClose: () => void;
  onNavigate: (index: number) => void;
}

const clampScale = (value: number) => Math.min(4, Math.max(1, value));

const FullScreenImage = ({ images, currentIndex, onClose, onNavigate }: FullScreenImageProps) => {
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const touchStartRef = useRef({ x: 0, y: 0, offsetX: 0, offsetY: 0 });
  const pinchStartRef = useRef<{ distance: number; scale: number } | null>(null);
  const currentImage = images[currentIndex];

  const resetZoom = useCallback(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  }, []);

  const navigateTo = useCallback((index: number) => {
    if (index < 0 || index >= images.length) return;
    resetZoom();
    onNavigate(index);
  }, [images.length, onNavigate, resetZoom]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowLeft") navigateTo(currentIndex - 1);
      if (event.key === "ArrowRight") navigateTo(currentIndex + 1);
      if (event.key === "+" || event.key === "=") setScale((value) => clampScale(value + 0.5));
      if (event.key === "-") setScale((value) => clampScale(value - 0.5));
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [currentIndex, navigateTo, onClose]);

  useEffect(() => {
    resetZoom();
  }, [currentIndex, resetZoom]);

  if (!currentImage) return null;

  const downloadCurrent = async () => {
    try {
      const response = await fetch(currentImage);
      if (!response.ok) throw new Error("Download failed");
      const blobUrl = URL.createObjectURL(await response.blob());
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `kindred-photo-${currentIndex + 1}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch {
      window.open(currentImage, "_blank", "noopener,noreferrer");
      toast.info("The photo opened in a new tab so you can save it.");
    }
  };

  const shareCurrent = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Kindred photo", url: currentImage });
      } catch {
        // The member closed the native share sheet.
      }
      return;
    }

    await navigator.clipboard.writeText(currentImage);
    toast.success("Photo link copied");
  };

  const getTouchDistance = (touches: React.TouchList) => {
    const first = touches[0];
    const second = touches[1];
    return Math.hypot(second.clientX - first.clientX, second.clientY - first.clientY);
  };

  const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    if (event.touches.length === 2) {
      pinchStartRef.current = { distance: getTouchDistance(event.touches), scale };
      return;
    }

    const touch = event.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY, offsetX: offset.x, offsetY: offset.y };
  };

  const handleTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
    if (event.touches.length === 2 && pinchStartRef.current) {
      event.preventDefault();
      const ratio = getTouchDistance(event.touches) / pinchStartRef.current.distance;
      setScale(clampScale(pinchStartRef.current.scale * ratio));
      return;
    }

    if (event.touches.length === 1 && scale > 1) {
      event.preventDefault();
      const touch = event.touches[0];
      setOffset({
        x: touchStartRef.current.offsetX + touch.clientX - touchStartRef.current.x,
        y: touchStartRef.current.offsetY + touch.clientY - touchStartRef.current.y,
      });
    }
  };

  const handleTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    pinchStartRef.current = null;
    if (scale > 1 || !event.changedTouches.length) return;

    const deltaX = event.changedTouches[0].clientX - touchStartRef.current.x;
    const deltaY = event.changedTouches[0].clientY - touchStartRef.current.y;
    if (Math.abs(deltaX) < 55 || Math.abs(deltaX) < Math.abs(deltaY)) return;
    navigateTo(deltaX > 0 ? currentIndex - 1 : currentIndex + 1);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Photo viewer"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/95"
      onClick={(event) => {
        if (event.currentTarget === event.target) onClose();
      }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className="absolute right-3 top-3 z-50 flex items-center gap-1 rounded-full bg-black/60 p-1 text-white">
        <button type="button" aria-label="Zoom out" onClick={() => setScale((value) => clampScale(value - 0.5))} className="rounded-full p-2 hover:bg-white/15">
          <ZoomOut size={20} />
        </button>
        <button type="button" aria-label="Reset zoom" onClick={resetZoom} className="rounded-full p-2 hover:bg-white/15">
          <RotateCcw size={19} />
        </button>
        <button type="button" aria-label="Zoom in" onClick={() => setScale((value) => clampScale(value + 0.5))} className="rounded-full p-2 hover:bg-white/15">
          <ZoomIn size={20} />
        </button>
        <button type="button" aria-label="Share photo" onClick={() => void shareCurrent()} className="rounded-full p-2 hover:bg-white/15">
          <Share2 size={20} />
        </button>
        <button type="button" aria-label="Download photo" onClick={() => void downloadCurrent()} className="rounded-full p-2 hover:bg-white/15">
          <Download size={20} />
        </button>
        <button type="button" aria-label="Close photo viewer" onClick={onClose} className="rounded-full p-2 hover:bg-white/15">
          <X size={22} />
        </button>
      </div>

      {images.length > 1 && currentIndex > 0 ? (
        <button type="button" aria-label="Previous photo" onClick={() => navigateTo(currentIndex - 1)} className="absolute left-2 z-40 rounded-full bg-black/55 p-2 text-white hover:bg-black/75">
          <ChevronLeft size={30} />
        </button>
      ) : null}
      {images.length > 1 && currentIndex < images.length - 1 ? (
        <button type="button" aria-label="Next photo" onClick={() => navigateTo(currentIndex + 1)} className="absolute right-2 z-40 rounded-full bg-black/55 p-2 text-white hover:bg-black/75">
          <ChevronRight size={30} />
        </button>
      ) : null}

      <div className="flex h-full w-full items-center justify-center overflow-hidden px-12 pb-14 pt-16">
        <img
          src={currentImage}
          alt={`Photo ${currentIndex + 1} of ${images.length}`}
          draggable={false}
          className="max-h-full max-w-full select-none object-contain transition-transform duration-150"
          style={{ transform: `translate3d(${offset.x}px, ${offset.y}px, 0) scale(${scale})`, touchAction: scale > 1 ? "none" : "pan-y" }}
          onDoubleClick={() => {
            if (scale > 1) resetZoom();
            else setScale(2);
          }}
          onWheel={(event) => {
            event.preventDefault();
            setScale((value) => clampScale(value + (event.deltaY < 0 ? 0.25 : -0.25)));
          }}
        />
      </div>

      {images.length > 1 ? (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-3 py-1.5 text-xs font-semibold text-white">
          {currentIndex + 1} / {images.length}
        </div>
      ) : null}
    </div>
  );
};

export default FullScreenImage;
